import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { z } from 'zod';
import {
  QUESTIONS,
  YES_NO_OPTIONS,
  isAsked,
  questionnaireAnswersSchema,
  textMaxChars,
  type Question,
  type QuestionnaireAnswers,
} from '@nemo/core/talent/questionnaire';
import { CONSENT_SENTENCE, PRIVACY_PATH } from '@nemo/core/talent/consent';
import { formatDay } from '../ui/format.js';
import { MultiSelect } from '../ui/MultiSelect.js';
import { Select } from '../ui/Select.js';
import { useToast } from '../ui/Toast.js';

/** Only to refuse early; the api's TALENT_CV_MAX_MB is the authority. */
const CV_MAX_MB = 10;

/** A long list (the countries) is searched rather than laid out as a grid. */
const MULTI_SEARCHABLE_OVER = 20;

const refusal = z.object({ error: z.string() });

const accepted = z.object({
  ok: z.literal(true),
  manageUrl: z.string(),
  schoolEmail: z.boolean(),
  complete: z.boolean(),
  expiresOn: z.string(),
});
type Accepted = z.infer<typeof accepted>;

const readAnswer = z.object({
  prefill: z.object({
    answers: z.unknown(),
    contact: z.object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
      linkedinUrl: z.string().nullable(),
    }),
  }),
});

interface Prefill {
  answers: QuestionnaireAnswers;
  contact: z.infer<typeof readAnswer>['prefill']['contact'];
}

const NOT_READ = 'The CV could not be read; fill the form yourself.';

function tooLarge(file: File): boolean {
  return file.size > CV_MAX_MB * 1_000_000;
}

const TOO_LARGE = `That file is larger than ${CV_MAX_MB} MB. Send a PDF or DOCX under that size.`;

/** Nothing is kept by the read: the same file goes again with the form. */
async function readCv(cv: File): Promise<Prefill> {
  const body = new FormData();
  body.set('cv', cv);
  const response = await fetch('/talent-api/cv-read', { method: 'POST', body }).catch(() => null);
  if (response === null) throw new Error(NOT_READ);
  const json: unknown = await response.json().catch(() => null);
  if (response.ok) {
    const parsed = readAnswer.safeParse(json);
    if (!parsed.success) throw new Error(NOT_READ);
    const answers = questionnaireAnswersSchema.safeParse(parsed.data.prefill.answers);
    return {
      answers: answers.success ? answers.data : {},
      contact: parsed.data.prefill.contact,
    };
  }
  const error = refusal.safeParse(json);
  throw new Error(error.success ? error.data.error : NOT_READ);
}

/** The form's own fields; the CV and the questionnaire travel beside them. */
const FORM_FIELDS = ['name', 'email', 'phone', 'linkedinUrl', 'note', 'consent'] as const;

async function submit(input: {
  form: HTMLFormElement;
  cv: File | null;
  answers: QuestionnaireAnswers;
}): Promise<Accepted> {
  const source = new FormData(input.form);
  if (input.cv === null) throw new Error('Attach your CV.');
  if (tooLarge(input.cv)) throw new Error(TOO_LARGE);
  const unanswered = QUESTIONS.find(
    (question) =>
      question.required &&
      isAsked(question, input.answers) &&
      input.answers[question.id] === undefined,
  );
  if (unanswered !== undefined) throw new Error(`Answer "${unanswered.label}".`);

  const body = new FormData();
  for (const field of FORM_FIELDS) {
    const value = source.get(field);
    if (value !== null) body.set(field, value);
  }
  body.set('cv', input.cv);
  const asked = Object.fromEntries(
    QUESTIONS.filter((question) => isAsked(question, input.answers)).map((question) => [
      question.id,
      input.answers[question.id],
    ]),
  );
  body.set('answers', JSON.stringify(asked));

  const response = await fetch('/talent-api/submissions', { method: 'POST', body });
  const json: unknown = await response.json().catch(() => null);
  if (response.ok) {
    const parsed = accepted.safeParse(json);
    if (parsed.success) return parsed.data;
    throw new Error('The upload went through, but the answer could not be read.');
  }
  const error = refusal.safeParse(json);
  throw new Error(error.success ? error.data.error : `The upload failed (${response.status}).`);
}

/** The second step's starting point: what the read found, or why there is nothing. */
type Filled = { prefill: Prefill; notice: null } | { prefill: null; notice: string | null };

/**
 * The public page a candidate sends a CV from: no session, so nothing here may
 * read one. The CV comes first and pre-fills the form; the candidate corrects
 * and confirms it.
 */
export function ApplyPage(): React.JSX.Element {
  const [cv, setCv] = useState<File | null>(null);
  const [filled, setFilled] = useState<Filled | null>(null);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});
  const [cvError, setCvError] = useState<string | null>(null);
  const read = useMutation({
    mutationFn: readCv,
    onSuccess: (prefill) => {
      setAnswers(prefill.answers);
      setFilled({ prefill, notice: null });
    },
    onError: (error) => setFilled({ prefill: null, notice: error.message }),
  });
  const upload = useMutation({ mutationFn: submit });

  const answer = (id: string, value: string | string[]) =>
    setAnswers((before) => ({
      ...before,
      [id]: value === '' || (Array.isArray(value) && value.length === 0) ? undefined : value,
    }));

  const intro = (
    <p className="subtle">
      Send Project A your CV, and recruiters working with its companies can find you.
    </p>
  );

  let content: React.JSX.Element;
  if (upload.isSuccess) {
    content = <Thanks result={upload.data} />;
  } else if (filled === null) {
    content = (
      <>
        {intro}
        <form
          className="apply-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (cv === null) return;
            if (tooLarge(cv)) {
              setCvError(TOO_LARGE);
              return;
            }
            setCvError(null);
            read.mutate(cv);
          }}
        >
          <label className="field">
            <span className="subtle">Your CV (DCP)</span>
            <input
              type="file"
              required
              accept=".pdf,.docx,application/pdf"
              onChange={(event) => setCv(event.target.files?.[0] ?? null)}
            />
            <span className="subtle apply-help">PDF or Word.</span>
          </label>
          <button className="button primary" type="submit" disabled={read.isPending}>
            {read.isPending ? 'Reading your CV…' : 'Read my CV'}
          </button>
          <span className="apply-inline">
            <button
              className="text-button"
              type="button"
              disabled={read.isPending}
              onClick={() => setFilled({ prefill: null, notice: null })}
            >
              Fill the form without reading
            </button>
          </span>
          {cvError !== null && <p className="signin-error">{cvError}</p>}
        </form>
      </>
    );
  } else {
    const contact = filled.prefill?.contact;
    content = (
      <>
        {intro}
        <form
          className="apply-form"
          onSubmit={(event) => {
            event.preventDefault();
            upload.mutate({ form: event.currentTarget, cv, answers });
          }}
        >
          {filled.prefill !== null && (
            <p className="subtle">
              We read these from your CV. Please check them and complete the rest.
            </p>
          )}
          {filled.notice !== null && <p className="subtle">{filled.notice}</p>}
          <CvField cv={cv} onChange={setCv} />
          <label className="field">
            <span className="subtle">Name</span>
            <input name="name" required maxLength={200} defaultValue={contact?.name ?? ''} />
          </label>
          <label className="field">
            <span className="subtle">Email</span>
            <input
              name="email"
              type="email"
              required
              maxLength={320}
              defaultValue={contact?.email ?? ''}
            />
            <span className="subtle apply-help">
              A personal address: a school one stops working when you leave.
            </span>
          </label>
          <label className="field">
            <span className="subtle">Phone (optional)</span>
            <input
              name="phone"
              type="tel"
              maxLength={40}
              placeholder="+49 30 1234567"
              defaultValue={contact?.phone ?? ''}
            />
          </label>
          <label className="field">
            <span className="subtle">LinkedIn (optional)</span>
            <input
              name="linkedinUrl"
              type="url"
              placeholder="https://www.linkedin.com/in/…"
              defaultValue={contact?.linkedinUrl ?? ''}
            />
          </label>
          {QUESTIONS.filter((question) => isAsked(question, answers)).map((question) => (
            <QuestionField
              key={question.id}
              question={question}
              value={answers[question.id]}
              onChange={(value) => answer(question.id, value)}
            />
          ))}
          <label className="field">
            <span className="subtle">Note (optional)</span>
            <textarea name="note" rows={3} maxLength={2000} />
          </label>
          <label className="apply-choice apply-consent">
            <input type="checkbox" name="consent" value="true" required />
            <span>
              {CONSENT_SENTENCE.before} {/* A new tab, so the form keeps what is filled in. */}
              <a href={PRIVACY_PATH} target="_blank" rel="noopener">
                {CONSENT_SENTENCE.link}
              </a>
              {CONSENT_SENTENCE.after}
            </span>
          </label>
          <button className="button primary" type="submit" disabled={upload.isPending}>
            {upload.isPending ? 'Sending…' : 'Send'}
          </button>
          {upload.isError && <p className="signin-error">{upload.error.message}</p>}
        </form>
      </>
    );
  }

  return (
    <div className="signin apply">
      <div className="signin-card">
        <h1>Talent Pool</h1>
        {content}
      </div>
    </div>
  );
}

/** The file chosen in the first step, kept to send with the form, and replaceable. */
function CvField(props: {
  cv: File | null;
  onChange: (cv: File | null) => void;
}): React.JSX.Element {
  const { cv, onChange } = props;
  const picker = useRef<HTMLInputElement>(null);
  if (cv === null) {
    return (
      <label className="field">
        <span className="subtle">Your CV (DCP)</span>
        <input
          type="file"
          required
          accept=".pdf,.docx,application/pdf"
          onChange={(event) => onChange(event.target.files?.[0] ?? null)}
        />
        <span className="subtle apply-help">PDF or Word.</span>
      </label>
    );
  }
  return (
    <div className="field">
      <span className="subtle">Your CV (DCP)</span>
      <span className="apply-inline">
        {cv.name} ·{' '}
        <button className="text-button" type="button" onClick={() => picker.current?.click()}>
          change
        </button>
      </span>
      <input
        ref={picker}
        type="file"
        hidden
        accept=".pdf,.docx,application/pdf"
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          if (chosen !== undefined) onChange(chosen);
        }}
      />
    </div>
  );
}

function fieldLabel(question: Question): string {
  return question.required ? question.label : `${question.label} (optional)`;
}

/** One question, in the control its type asks for. */
function QuestionField(props: {
  question: Question;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
}): React.JSX.Element {
  const { question, value, onChange } = props;
  const text = typeof value === 'string' ? value : '';
  const help =
    question.help === undefined ? null : <span className="subtle apply-help">{question.help}</span>;

  switch (question.type) {
    case 'text':
      return (
        <label className="field">
          <span className="subtle">{fieldLabel(question)}</span>
          {question.multiline === true ? (
            <textarea
              rows={3}
              value={text}
              maxLength={textMaxChars(question)}
              required={question.required}
              onChange={(event) => onChange(event.target.value)}
            />
          ) : (
            <input
              value={text}
              required={question.required}
              maxLength={textMaxChars(question)}
              onChange={(event) => onChange(event.target.value)}
            />
          )}
          {help}
        </label>
      );
    case 'month':
      return (
        <label className="field">
          <span className="subtle">{fieldLabel(question)}</span>
          <input
            type="month"
            value={text}
            required={question.required}
            placeholder="YYYY-MM"
            pattern="\d{4}-\d{2}"
            onChange={(event) => onChange(event.target.value)}
          />
          {help}
        </label>
      );
    case 'yes_no':
      return (
        <fieldset className="field apply-choices">
          <legend className="subtle">{fieldLabel(question)}</legend>
          <span className="apply-inline">
            {YES_NO_OPTIONS.map((option) => (
              <label key={option.value} className="apply-choice">
                <input
                  type="radio"
                  aria-label={option.label}
                  name={`answer-${question.id}`}
                  value={option.value}
                  checked={text === option.value}
                  required={question.required}
                  onChange={() => onChange(option.value)}
                />
                {option.label}
              </label>
            ))}
          </span>
          {help}
        </fieldset>
      );
    case 'single':
      return (
        <div className="field">
          <span className="subtle">{fieldLabel(question)}</span>
          <Select
            label={question.label}
            value={text}
            placeholder="Choose…"
            onChange={onChange}
            options={question.options}
          />
          {help}
        </div>
      );
    case 'multi': {
      const chosen = Array.isArray(value) ? value : [];
      const toggle = (code: string) =>
        onChange(
          chosen.includes(code) ? chosen.filter((each) => each !== code) : [...chosen, code],
        );
      if (question.options.length > MULTI_SEARCHABLE_OVER) {
        return (
          <div className="field">
            <span className="subtle">{fieldLabel(question)}</span>
            <MultiSelect
              label={question.label}
              selected={chosen}
              options={question.options}
              onToggle={toggle}
              onClear={() => onChange([])}
              placeholder="Choose…"
              noun="answers"
            />
            {help}
          </div>
        );
      }
      return (
        <fieldset className="field apply-choices">
          <legend className="subtle">{fieldLabel(question)}</legend>
          {help}
          <span className="apply-checkboxes">
            {question.options.map((option) => (
              <label key={option.value} className="apply-choice">
                <input
                  type="checkbox"
                  aria-label={option.label}
                  checked={chosen.includes(option.value)}
                  onChange={() => toggle(option.value)}
                />
                {option.label}
              </label>
            ))}
          </span>
        </fieldset>
      );
    }
    default:
      return unreachable(question);
  }
}

function unreachable(value: never): never {
  throw new Error(`Unhandled question: ${JSON.stringify(value)}`);
}

/** After a submission: the manage link, shown once, and what the profile still lacks. */
function Thanks(props: { result: Accepted }): React.JSX.Element {
  const { result } = props;
  const toast = useToast();
  const linkInput = useRef<HTMLInputElement>(null);
  // Without the clipboard (an http origin, an old browser) the link is left selected to copy by hand.
  const selectLink = () => {
    linkInput.current?.focus();
    linkInput.current?.select();
    toast('Select the link and copy it');
  };
  const copy = () => {
    if (!('clipboard' in navigator) || !navigator.clipboard) {
      selectLink();
      return;
    }
    navigator.clipboard.writeText(result.manageUrl).then(() => toast('Link copied'), selectLink);
  };
  return (
    <>
      <p>Thanks, we have your CV.</p>
      <p className="subtle">
        We keep your profile until {formatDay(result.expiresOn)} and then delete it. You can remove
        it sooner with the link below.
      </p>
      {!result.complete && (
        <p className="subtle">
          Recruiters will not see your profile until every required question is answered. To finish
          it, remove this profile with the link below, then send the form again with every required
          question answered.
        </p>
      )}
      {result.schoolEmail && (
        <p className="signin-error">
          That looks like a school address, which stops working when you leave. If you have a
          personal one, remove this profile with the link below and send the form again with it.
        </p>
      )}
      <p>
        <strong>Save this link: it is the only way to remove your profile.</strong>
      </p>
      <input
        ref={linkInput}
        readOnly
        aria-label="Your profile link"
        value={result.manageUrl}
        onFocus={(event) => event.currentTarget.select()}
      />
      <span className="apply-inline">
        <button className="button" type="button" onClick={copy}>
          Copy link
        </button>
      </span>
    </>
  );
}
